import { useCallback, useRef, useEffect, useMemo } from "react";
import { Card, Label, TextInput } from "flowbite-react";
import { HiRefresh } from "react-icons/hi";
import { useTranslation } from "@repo/core";
import {
  useBuckets,
  useUniverseFilterData,
} from "../../../hooks/api/useUniversesApi";
import type {
  FilterCriteria,
} from "../../../types/factor-mimicking";

interface FactorFiltersProps {
  filters: FilterCriteria;
  onFiltersChange: (filters: FilterCriteria) => void;
  onReset: () => void;
  className?: string;
  // Period selection props
  selectedPeriod?: string;
  onPeriodChange?: (period: string) => void;
}

/**
 * Factor filters component - can be used both in sidebar and modal
 */
export const FactorFilters = ({
  filters,
  onFiltersChange,
  onReset,
  className = "",
  selectedPeriod = "5Y",
  onPeriodChange,
}: FactorFiltersProps) => {
  const { tMimicking, tCommon } = useTranslation();
  const { data: buckets, isLoading: bucketsLoading } = useBuckets();
  const { data: universeFilterData } = useUniverseFilterData();

  // strategy.style stores a stable, lowercase English identifier (e.g.
  // "quality"), while /api/v1/buckets returns display text already
  // localized for the current language (e.g. "Kvalita"). There's no stable
  // id alongside that localized text, so resolve each downloaded label back
  // to its stable key via the app's own i18n bundle - which already encodes
  // this exact mapping - purely for filtering, not for display.
  const STYLE_KEYS = [
    "value",
    "momentum",
    "volatility",
    "trend",
    "quality",
    "size",
  ] as const;

  const styleOptions = useMemo(() => {
    const labels =
      buckets || [
        "Valuation",
        "Profitability",
        "Growth",
        "Financial Health",
        "Risk",
        "Technical",
        "Size",
      ];
    return labels.map((label) => {
      // Case-insensitive: the S3 source data's casing isn't consistent
      // across language files (e.g. English lowercase vs. Czech capitalized).
      const key = STYLE_KEYS.find(
        (candidate) =>
          tMimicking(`filters.style.${candidate}`).toLowerCase() ===
          label.toLowerCase()
      );
      return { key: key || label, label };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buckets, tMimicking]);

  // Use ref to store current filters to avoid callback dependency issues
  const filtersRef = useRef(filters);

  // Update ref when filters change
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Period options
  const periodOptions = [
    { value: "YTD", label: tCommon("periods.YTD") },
    { value: "1Y", label: tCommon("periods.1Y") },
    { value: "3Y", label: tCommon("periods.3Y") },
    { value: "5Y", label: tCommon("periods.5Y") },
    { value: "ALL", label: tCommon("periods.ALL") },
  ];

  const handlePeriodChange = useCallback(
    (period: string) => {
      if (onPeriodChange) {
        onPeriodChange(period);
      }
    },
    [onPeriodChange]
  );

  const handleIndicatorChange = useCallback(
    (value: string) => {
      const newFilters = {
        ...filtersRef.current,
        indicator: value || undefined,
      };
      onFiltersChange(newFilters);
    },
    [onFiltersChange]
  );

  const handleStyleChange = useCallback(
    (value: string, checked: boolean) => {
      const currentStyles = filtersRef.current.style || [];
      let newStyles: string[];

      if (checked) {
        // Add the style if it's not already selected
        newStyles = currentStyles.includes(value)
          ? currentStyles
          : [...currentStyles, value];
      } else {
        // Remove the style from selection
        newStyles = currentStyles.filter((style) => style !== value);
      }

      onFiltersChange({
        ...filtersRef.current,
        style: newStyles.length > 0 ? newStyles : undefined,
      });
    },
    [onFiltersChange]
  );

  const handleStyleSelectAll = useCallback(() => {
    onFiltersChange({
      ...filtersRef.current,
      style: styleOptions.map((option) => option.key),
    });
  }, [onFiltersChange, styleOptions]);

  const handleStyleClearAll = useCallback(() => {
    onFiltersChange({
      ...filtersRef.current,
      style: undefined,
    });
  }, [onFiltersChange]);

  const handleSharpeRatioOperatorChange = useCallback(
    (operator: "lt" | "eq" | "gt" | "") => {
      if (!operator) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { sharpeRatio, ...newFilters } = filtersRef.current;
        onFiltersChange(newFilters);
      } else if (filtersRef.current.sharpeRatio) {
        onFiltersChange({
          ...filtersRef.current,
          sharpeRatio: {
            ...filtersRef.current.sharpeRatio,
            operator,
          },
        });
      } else {
        onFiltersChange({
          ...filtersRef.current,
          sharpeRatio: {
            operator,
            value: 1.0,
          },
        });
      }
    },
    [onFiltersChange]
  );

  const handleSharpeRatioValueChange = useCallback(
    (value: number) => {
      if (filtersRef.current.sharpeRatio) {
        onFiltersChange({
          ...filtersRef.current,
          sharpeRatio: {
            ...filtersRef.current.sharpeRatio,
            value,
          },
        });
      }
    },
    [onFiltersChange]
  );

  const handleVolatilityOperatorChange = useCallback(
    (operator: "lt" | "eq" | "gt" | "") => {
      if (!operator) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { volatility, ...newFilters } = filtersRef.current;
        onFiltersChange(newFilters);
      } else if (filtersRef.current.volatility) {
        onFiltersChange({
          ...filtersRef.current,
          volatility: {
            ...filtersRef.current.volatility,
            operator,
          },
        });
      } else {
        onFiltersChange({
          ...filtersRef.current,
          volatility: {
            operator,
            value: 15.0, // Default 15% volatility
          },
        });
      }
    },
    [onFiltersChange]
  );

  const handleVolatilityValueChange = useCallback(
    (value: number) => {
      if (filtersRef.current.volatility) {
        onFiltersChange({
          ...filtersRef.current,
          volatility: {
            ...filtersRef.current.volatility,
            value,
          },
        });
      }
    },
    [onFiltersChange]
  );

  const handleCAGROperatorChange = useCallback(
    (operator: "lt" | "eq" | "gt" | "") => {
      if (!operator) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { cagr, ...newFilters } = filtersRef.current;
        onFiltersChange(newFilters);
      } else if (filtersRef.current.cagr) {
        onFiltersChange({
          ...filtersRef.current,
          cagr: {
            ...filtersRef.current.cagr,
            operator,
          },
        });
      } else {
        onFiltersChange({
          ...filtersRef.current,
          cagr: {
            operator,
            value: 10.0, // Default 10% CAGR
          },
        });
      }
    },
    [onFiltersChange]
  );

  const handleCAGRValueChange = useCallback(
    (value: number) => {
      if (filtersRef.current.cagr) {
        onFiltersChange({
          ...filtersRef.current,
          cagr: {
            ...filtersRef.current.cagr,
            value,
          },
        });
      }
    },
    [onFiltersChange]
  );

  const handleMeanTurnoverOperatorChange = useCallback(
    (operator: "lt" | "eq" | "gt" | "") => {
      if (!operator) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { meanTurnover, ...newFilters } = filtersRef.current;
        onFiltersChange(newFilters);
      } else if (filtersRef.current.meanTurnover) {
        onFiltersChange({
          ...filtersRef.current,
          meanTurnover: {
            ...filtersRef.current.meanTurnover,
            operator,
          },
        });
      } else {
        onFiltersChange({
          ...filtersRef.current,
          meanTurnover: {
            operator,
            value: 50.0, // Default 50% turnover
          },
        });
      }
    },
    [onFiltersChange]
  );

  const handleMeanTurnoverValueChange = useCallback(
    (value: number) => {
      if (filtersRef.current.meanTurnover) {
        onFiltersChange({
          ...filtersRef.current,
          meanTurnover: {
            ...filtersRef.current.meanTurnover,
            value,
          },
        });
      }
    },
    [onFiltersChange]
  );

  const handleReset = useCallback(() => {
    onReset();
  }, [onReset]);

  if (!universeFilterData) {
    return null;
  }

  return (
    <Card className={`h-fit w-full ${className}`} data-tour="factor-filters">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {tMimicking("filters.title")}
          </h3>
          <button
            type="button"
            onClick={handleReset}
            className="flex items-center gap-2 px-3 py-2 text-blue-600 hover:text-blue-800 transition-colors duration-200 cursor-pointer"
          >
            <HiRefresh size={16} />
            {tMimicking("filters.reset")}
          </button>
        </div>

        <hr className="border-gray-200 mb-6" />

        {/* Filter Form */}
        <form className="space-y-4 max-w-full">
          {/* Period Selection */}
          <div>
            <Label className="block text-sm font-medium text-gray-700 mb-2">
              {tMimicking("filters.period.label")}
            </Label>
            <div className="flex flex-wrap gap-1">
              {periodOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handlePeriodChange(option.value)}
                  className={`px-3 py-1 text-sm rounded transition-colors ${selectedPeriod === option.value
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Indicator Input */}
          <div>
            <Label
              htmlFor="indicator"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              {tMimicking("filters.indicator.label")}
            </Label>
            <TextInput
              id="indicator"
              key="indicator-input"
              type="text"
              placeholder={tMimicking("filters.indicator.placeholder")}
              value={filters.indicator || ""}
              onChange={(e) => handleIndicatorChange(e.target.value)}
              className="max-w-full"
            />
          </div>

          {/* Group Multi-select */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="block text-sm font-medium text-gray-700">
                {tMimicking("filters.style.label")}
              </Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleStyleSelectAll}
                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  {tMimicking("filters.style.selectAll")}
                </button>
                <span className="text-xs text-gray-400">|</span>
                <button
                  type="button"
                  onClick={handleStyleClearAll}
                  className="text-xs text-gray-600 hover:text-gray-800 underline"
                >
                  {tMimicking("filters.style.clearAll")}
                </button>
              </div>
            </div>
            <div className="space-y-2 overflow-y-auto border border-gray-300 rounded-lg p-2">
              {bucketsLoading ? (
                <div className="text-sm text-gray-500 p-2">
                  {tMimicking("filters.loading")}
                </div>
              ) : (
                styleOptions.map(({ key, label }) => (
                  <div key={key} className="flex items-center">
                    <input
                      id={`style-${key}`}
                      name={`style-${key}`}
                      type="checkbox"
                      checked={filters.style?.includes(key) || false}
                      onChange={(e) =>
                        handleStyleChange(key, e.target.checked)
                      }
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <Label
                      htmlFor={`style-${key}`}
                      className="ml-2 text-sm text-gray-700 cursor-pointer capitalize"
                    >
                      {/* /api/v1/buckets already returns text localized for
                          the current language - filtering uses the resolved
                          stable key (see styleOptions), not this label */}
                      {label}
                    </Label>
                  </div>
                ))
              )}
            </div>
            {filters.style && filters.style.length > 0 && (
              <div className="mt-2 text-xs text-gray-600">
                {filters.style.length} style
                {filters.style.length > 1 ? "s" : ""} selected
              </div>
            )}
          </div>

          {/* Sharpe Ratio Combined Input */}
          <div>
            <Label className="block text-sm font-medium text-gray-700 mb-2">
              {tMimicking("filters.sharpeRatio.label")}
            </Label>
            <div className="flex">
              <select
                value={filters.sharpeRatio?.operator || ""}
                onChange={(e) =>
                  handleSharpeRatioOperatorChange(
                    e.target.value as "lt" | "eq" | "gt" | ""
                  )
                }
                className="flex-shrink-0 border border-gray-300 bg-gray-50 text-gray-900 text-sm rounded-l-lg focus:ring-blue-500 focus:border-blue-500 ps-2"
              >
                <option value="">
                  {tMimicking("filters.volatility.operatorPlaceholder")}
                </option>
                <option value="lt">
                  {tMimicking("filters.volatility.lessThan")}
                </option>
                <option value="eq">
                  {tMimicking("filters.volatility.equalTo")}
                </option>
                <option value="gt">
                  {tMimicking("filters.volatility.greaterThan")}
                </option>
              </select>
              <TextInput
                type="number"
                step="0.01"
                placeholder="1.0"
                value={filters.sharpeRatio?.value || ""}
                onChange={(e) =>
                  handleSharpeRatioValueChange(parseFloat(e.target.value) || 0)
                }
                disabled={!filters.sharpeRatio?.operator}
                className="flex-1 min-w-0 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          {/* Volatility Combined Input */}
          <div>
            <Label className="block text-sm font-medium text-gray-700 mb-2">
              {tMimicking("filters.volatility.label")}
            </Label>
            <div className="flex">
              <select
                value={filters.volatility?.operator || ""}
                onChange={(e) =>
                  handleVolatilityOperatorChange(
                    e.target.value as "lt" | "eq" | "gt" | ""
                  )
                }
                className="flex-shrink-0 border border-gray-300 bg-gray-50 text-gray-900 text-sm rounded-l-lg focus:ring-blue-500 focus:border-blue-500 ps-2"
              >
                <option value="">
                  {tMimicking("filters.volatility.operatorPlaceholder")}
                </option>
                <option value="lt">
                  {tMimicking("filters.volatility.lessThan")}
                </option>
                <option value="eq">
                  {tMimicking("filters.volatility.equalTo")}
                </option>
                <option value="gt">
                  {tMimicking("filters.volatility.greaterThan")}
                </option>
              </select>
              <TextInput
                type="number"
                step="0.1"
                placeholder="15.0"
                value={filters.volatility?.value || ""}
                onChange={(e) =>
                  handleVolatilityValueChange(parseFloat(e.target.value) || 0)
                }
                disabled={!filters.volatility?.operator}
                className="flex-1 min-w-0 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          {/* CAGR Combined Input */}
          <div>
            <Label className="block text-sm font-medium text-gray-700 mb-2">
              {tMimicking("filters.cagr.label")}
            </Label>
            <div className="flex">
              <select
                value={filters.cagr?.operator || ""}
                onChange={(e) =>
                  handleCAGROperatorChange(
                    e.target.value as "lt" | "eq" | "gt" | ""
                  )
                }
                className="flex-shrink-0 border border-gray-300 bg-gray-50 text-gray-900 text-sm rounded-l-lg focus:ring-blue-500 focus:border-blue-500 ps-2"
              >
                <option value="">
                  {tMimicking("filters.cagr.operatorPlaceholder")}
                </option>
                <option value="lt">
                  {tMimicking("filters.cagr.lessThan")}
                </option>
                <option value="eq">{tMimicking("filters.cagr.equalTo")}</option>
                <option value="gt">
                  {tMimicking("filters.cagr.greaterThan")}
                </option>
              </select>
              <TextInput
                type="number"
                step="0.1"
                placeholder="10.0"
                value={filters.cagr?.value || ""}
                onChange={(e) =>
                  handleCAGRValueChange(parseFloat(e.target.value) || 0)
                }
                disabled={!filters.cagr?.operator}
                className="flex-1 min-w-0 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          {/* Mean Turnover Combined Input */}
          <div>
            <Label className="block text-sm font-medium text-gray-700 mb-2">
              {tMimicking("filters.meanTurnover.label")}
            </Label>
            <div className="flex">
              <select
                value={filters.meanTurnover?.operator || ""}
                onChange={(e) =>
                  handleMeanTurnoverOperatorChange(
                    e.target.value as "lt" | "eq" | "gt" | ""
                  )
                }
                className="flex-shrink-0 border border-gray-300 bg-gray-50 text-gray-900 text-sm rounded-l-lg focus:ring-blue-500 focus:border-blue-500 ps-2"
              >
                <option value="">
                  {tMimicking("filters.meanTurnover.operatorPlaceholder")}
                </option>
                <option value="lt">
                  {tMimicking("filters.meanTurnover.lessThan")}
                </option>
                <option value="eq">
                  {tMimicking("filters.meanTurnover.equalTo")}
                </option>
                <option value="gt">
                  {tMimicking("filters.meanTurnover.greaterThan")}
                </option>
              </select>
              <TextInput
                type="number"
                step="0.1"
                placeholder="50.0"
                value={filters.meanTurnover?.value || ""}
                onChange={(e) =>
                  handleMeanTurnoverValueChange(parseFloat(e.target.value) || 0)
                }
                disabled={!filters.meanTurnover?.operator}
                className="flex-1 min-w-0 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
          </div>
        </form>
      </div>
    </Card>
  );
};

export default FactorFilters;
